/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

interface AppFooterState {
  /** Whether a page has asked for the footer to be left off. */
  hidden: boolean;
  hide: () => void;
  show: () => void;
}

const AppFooterContext = createContext<AppFooterState>({
  hidden: false,
  hide: () => {},
  show: () => {},
});

/**
 * The footer under every page, and the pages that do without it.
 *
 * The same arrangement the breadcrumb and the bottom pill have: the chrome
 * belongs to the app, whether it is wanted is the page's to say. Said by the
 * page rather than worked out from the URL here, because a climb alone is
 * reached by a dozen paths (gyms, crags, boards, competitions, their videos and
 * their editors) and a list of them kept in the shell is a list to forget one in.
 */
export function AppFooterProvider({ children }: { children: React.ReactNode }) {
  // Counted rather than a flag, so two pages overlapping through a transition
  // cannot have the one unmounting bring the footer back under the one that is
  // still without it.
  const [hiddenBy, setHiddenBy] = useState(0);
  const hide = useCallback(() => setHiddenBy((count) => count + 1), []);
  const show = useCallback(() => setHiddenBy((count) => Math.max(0, count - 1)), []);

  const value = useMemo(() => ({ hidden: hiddenBy > 0, hide, show }), [hiddenBy, hide, show]);

  return <AppFooterContext.Provider value={value}>{children}</AppFooterContext.Provider>;
}

export function useAppFooter() {
  return useContext(AppFooterContext);
}

/** Leave the footer off for as long as this is mounted. */
export function useHideAppFooter() {
  const { hide, show } = useAppFooter();

  useEffect(() => {
    hide();
    return show;
  }, [hide, show]);
}
